import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, tap, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { HomeService } from './../../home.service';
import * as HomeActions from './../actions/home.actions';
import { selectCalendarEvents, selectEmployeeHours, selectEmployeeStatus } from '../selectors/home.selectors';
import { CalendarService } from '@app/core/services/calendar.service';

@Injectable()
export class HomeEffects {
  private readonly homeService = inject(HomeService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly calendarService = inject(CalendarService);

  loadCalendarEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.loadCalendarEvents),
      withLatestFrom(this.store.select(selectCalendarEvents)), // Get current state
      mergeMap(([action, cachedEvents]) => {
        const { employeeNumber, selectedDate } = action;
        // If data is already loaded, don't trigger the API
        // console.log('Loading employee event data', cachedEvents);
        // if (cachedEvents && cachedEvents.length > 0) {
        //   return of(HomeActions.loadCalendarEventsSuccess({ calendarEvents: cachedEvents }));
        // }

        // Otherwise, fetch data from API
        return this.homeService.getCalendarEvents(employeeNumber, selectedDate).pipe(
          tap((response) => {
            // Optional: Add caching mechanism
            this.calendarService.setData(response);
          }),
          map((calendarEvents) =>
            HomeActions.loadCalendarEventsSuccess({ calendarEvents })
          ),
          catchError((error) =>
            of(HomeActions.loadCalendarEventsFailure({ error: error.message }))
          )
        );
      })
    )
  );

  loadEmployeeHours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.loadEmployeeHours),
      withLatestFrom(this.store.select(selectEmployeeHours)), // Check current state
      mergeMap(([action, cachedHours]) => {
        const { employeeNumber, date } = action;

        // If data exists for the requested date, use cached data
        if (cachedHours && cachedHours.length > 0) {
          return of(HomeActions.loadEmployeeHoursSuccess({ employeeHours: cachedHours }));
        }

        // Otherwise, fetch from API
        return this.homeService.getEmployeeHours(employeeNumber, date).pipe(
          map((employeeHours) =>
            HomeActions.loadEmployeeHoursSuccess({ employeeHours })
          ),
          catchError((error) =>
            of(HomeActions.loadEmployeeHoursFailure({ error: error.message }))
          )
        );
      })
    )
  );

  loadEmployeeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.loadEmployeeStatus),
      withLatestFrom(this.store.select(selectEmployeeStatus)), // Check current state
      mergeMap(([action, cachedStatus]) => {
        const { employeeNumber } = action;

        // If status is already loaded, use cached data
        if (cachedStatus) {
          return of(HomeActions.loadEmployeeStatusSuccess({ clockStatus: cachedStatus }));
        }

        // Otherwise, fetch from API
        return this.homeService.getEmployeeStatus(employeeNumber).pipe(
          map((clockStatus) =>
            HomeActions.loadEmployeeStatusSuccess({ clockStatus })
          ),
          catchError((error) =>
            of(HomeActions.loadEmployeeStatusFailure({ error: error.message }))
          )
        );
      })
    )
  );
}
